<template>
    <div>
        <app-bar :title="collection.name"/>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <div class="pt-8 pb-12 px-8" style="width: 100%">
                <div class="pb-10">
                    <v-btn
                        @click="edit"
                        class="sm mr-5"
                        color="primary"
                    >{{ t('general_action_edit') }}
                    </v-btn>
                    <v-btn
                        class="sm mr-5"
                        color="primary"
                        outlined
                        @click.prevent="suspend(collection)"
                    >
                        {{ collection.enabled ? t('general_action_suspend') : t('general_action_unsuspend') }}
                    </v-btn>
                    <confirm-modal
                        :title="t('general_title_confirm_action')"
                        @agree="del(collection)"
                    >
                        <template #content>
                            <div class="text-center">{{ t('collection_warning_delete') }}</div>
                        </template>
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm"
                                color="orange"
                                outlined
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('general_action_delete') }}</v-btn>
                        </template>
                    </confirm-modal>
                </div>
                <div class="float-left mr-10" style="width: 394px; max-width: 394px">
                    <img
                        v-if="collection.photoUrl"
                        :src="collection.photoUrl"
                        width="394"
                    />
                    <div class="form-subtitle">{{ t('collection_label_description') }}:</div>
                    <div class="form-subtitle-description" v-html="collection.description"></div>

                    <card-unit class="mt-6" :items="items()">
                        <template #title>{{ t('collection_label_id') }}: {{ collection.id }}</template>
                    </card-unit>
                </div>
                <div class="float-left" style="max-width: calc(100% - 434px)">
                    <card-collection
                        :meta="workoutsMeta"
                        :busy="workoutsBusy"
                        @updated="onWorkoutsPage"
                    >
                        <template #data>
                            <thead>
                            <tr>
                                <th>{{ t('workout_label_id') }}</th>
                                <th>{{ t('workout_label_name') }}</th>
                                <th>{{ t('workout_label_stroke') }}</th>
                                <th>{{ t('collection_label_level') }}</th>
                                <th>{{ t('workout_label_duration') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="workouts.data.length === 0">
                                <td class="text-center" colspan="5">{{ t('general_text_no_data') }}</td>
                            </tr>
                            <tr v-for="workout in workouts.data">
                                <td class="titled">{{ workout.id }}</td>
                                <td class="titled">
                                    <a
                                        :href="`/workouts/${workout.id}/view`"
                                        target="_blank"
                                    >{{ workout.name || `#${workout.id}` }}</a>
                                </td>
                                <td>{{ workout.stroke.name }}</td>
                                <td>{{ levelTitle(workout.level) }}</td>
                                <td>{{ time.format(workout.workoutTotalTime) }}</td>
                            </tr>
                            </tbody>
                        </template>
                    </card-collection>
                </div>
            </div>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useEnums, useStrokes } from '@/core/defaults'
    import { WorkoutCollectionModel } from '@/core/models/workout-collection'
    import { WorkoutCollection as WorkoutList } from '@/store/modules/workout-collection'
    import * as time from '@/core/utils/time-helper'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import CardCollection from '@/components/CardCollection.vue'
    import CardUnit from '@/components/CardUnit.vue'

    export default defineComponent({
        components: {
            ConfirmModal,
            CardCollection,
            CardUnit
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const enums = useEnums()
            const strokeEnums = useStrokes()
            const id = router.currentRoute.params['id']

            const state = reactive({
                overlay: true,
                workoutsBusy: true,
                workoutsMeta: undefined,
            })

            // Collection's workouts are paginated through the Workout API filtered by collection_id.
            const workoutQueries = reactive({
                collection_id: id,
                page: 1,
                per_page: 10,
                relations: 'stroke',
            })

            const collectionModel = computed(() => <WorkoutCollectionModel>store.getters['workoutCollectionItem/get'])
            const workouts = computed(() => <WorkoutList>store.getters['workoutCollection/get'])

            const capture = () => {
                store.dispatch('workoutCollectionItem/get', { id: id, payload: { relations: 'strokes' } }).then(() => {
                    state.overlay = false
                })
            }

            const fetchWorkouts = () => {
                state.workoutsBusy = true
                store.dispatch('workoutCollection/get', workoutQueries).then((data: any) => {
                    state.workoutsMeta = data.meta
                    state.workoutsBusy = false
                })
            }

            const onWorkoutsPage = (updatedQueries: any) => {
                workoutQueries.page = updatedQueries.page
                fetchWorkouts()
            }

            const suspend = (model: WorkoutCollectionModel) => {
                state.overlay = true
                store.dispatch('workoutCollectionItem/update', {
                    id: model.id,
                    payload: { enabled: ! model.enabled }
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                    capture()
                })
            }

            const edit = () => router.push({ name: 'workout-collections.show', params: { id: <any>collectionModel.value.id } })

            const del = (model: WorkoutCollectionModel) => {
                state.overlay = true
                store.dispatch('workoutCollectionItem/delete', { id: model.id }).then(() => {
                    router.push({ name: 'workout-collections' })
                })
            }

            const items = () => {
                return <any>[
                    { text: i18n.t('collection_label_level'), value: enums.levelTitle(collectionModel.value.level) },
                    { text: i18n.t('collection_label_strokes'), value: strokeEnums.strokesStrTitle(collectionModel.value.strokeIds) },
                    { text: i18n.t('collection_label_workouts'), value: collectionModel.value.workoutCount },
                    { text: i18n.t('collection_label_sort_order'), value: collectionModel.value.sortOrder },
                    { text: i18n.t('general_label_creation_date'), value: collectionModel.value.createdAt.format('DD/MM/YYYY') },
                    { text: i18n.t('general_action_published'), value: collectionModel.value.enabled ? i18n.t('general_text_yes') : i18n.t('general_text_no') },
                ]
            }

            onMounted(() => {
                workouts.value.data = []
                capture()
                fetchWorkouts()
            })

            return {
                capture,
                items,
                time,
                collection: collectionModel.value,
                workouts: workouts.value,
                onWorkoutsPage,
                edit,
                del,
                suspend,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state)
            }
        }
    })
</script>
